import { useState, useRef, useEffect, useCallback } from 'react';
import classes from './SchedulePickupPage.module.css';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { pageContentConstants } from '../../constants/PageContentConstants';
import {
  SCHEDULE_PICKUP_SHEET_ID,
  CLIENT_EMAIL,
  PRIVATE_KEY,
  doc,
  A_SCHEDULE_PICKUP_SHEET_ID,
  A_CLIENT_EMAIL,
  A_PRIVATE_KEY,
  adoc,
  getCurrentDate,
  getCurrentDay,
} from '../../shared/gsheetConstants';
import './SchedulePickupPage.module.css';

const SchedulePickupPage = (props) => {
  const formFields = pageContentConstants.SCHEDULE_PICKUP_FORM_CONTENT.FORM_INPUT_FIELDS;
  const stores = pageContentConstants.SCHEDULE_PICKUP_FORM_CONTENT.STORES;
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [isRequested, setIsRequested] = useState(false);
  const [yourName, setYourName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  const now = new Date();
  const bufferTime = new Date(now.getTime() + 30 * 60000); // 30 minutes from now
  const earlyHour = 10;
  const lateHour = 19;
  let adjustedPickupDate = new Date(bufferTime);

  // If time is after 7 PM → next day 10 AM
  if (adjustedPickupDate.getHours() >= lateHour) {
    adjustedPickupDate.setDate(adjustedPickupDate.getDate() + 1);
    adjustedPickupDate.setHours(10, 0, 0, 0); // Set to 10:00 AM
  }

  // If time is before 10 AM → today at 10 AM
  else if (adjustedPickupDate.getHours() < earlyHour) {
    adjustedPickupDate.setHours(10, 0, 0, 0); // Today at 10:00 AM
  }

  // Else leave the 30-minute buffer time as-is

  const [pickupDate, setPickupDate] = useState(adjustedPickupDate);

  // Delivery date is 2 days after pickup
  const deliveryDateCalc = new Date(adjustedPickupDate);
  deliveryDateCalc.setDate(deliveryDateCalc.getDate() + 2);

  const [deliveryDate, setDeliveryDate] = useState(deliveryDateCalc);

  const [address, setAddress] = useState('');
  const [store, setStore] = useState('Velachery');
  const [formErrors, setFormErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});

  // Set minTime = 10:00 AM
  const minTime = new Date();
  minTime.setHours(10, 0, 0); // 10:00 AM

  // Set maxTime = 7:00 PM
  const maxTime = new Date();
  maxTime.setHours(19, 0, 0); // 7:00 PM

  const toast = useRef(null);

  function openModal() {
    setModalIsOpen(true);
  }

  function closeModal() {
    setModalIsOpen(false);
  }

  const showToast = (severity, summary, detail) => {
    toast.current.show({ severity, summary, detail, life: 2000 });
  };

  // Track which fields have been touched
  const handleTouch = (field) => {
    setTouchedFields({ ...touchedFields, [field]: true });
  };

  const validateFields = useCallback(() => {
    let errors = {};
    if (!yourName.trim()) errors.name = 'Please enter your name';
    if (!phone.trim()) errors.phone = 'Please enter your phone number';
    if (!email.trim()) errors.email = 'Please enter a valid email';
    if (!pickupDate) errors.pickupDate = 'Please select a pickup date';
    if (!address.trim()) errors.address = 'Please enter your address';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [address, yourName, email, pickupDate, phone]);


  useEffect(() => {
    setIsFormValid(validateFields()); // Set form validity whenever fields change
  }, [validateFields]);


  const getMethod = (e, field) => {
    switch (field) {
      case formFields[0]:
        return setYourName(e.target.value);
      case formFields[1]:
        return setPhone(e.target.value);
      case formFields[3]:
        return setEmail(e.target.value);
      case formFields[2]:
        return setAddress(e.target.value);
      default:
        return;
    }
  };

  const validateDeliveryDateTime = (value) => {
    const date = new Date(value);
    const deliveryDate = date.setDate(date.getDate() + 2);
    setDeliveryDate(new Date(deliveryDate));
  };

  async function handleSubmit(event) {
    event.preventDefault();
    if (!validateFields()) return; // Custom validation
    setIsRequested(true);

    const newEntry = {
      Date: getCurrentDate(),
      Day: getCurrentDay(),
      Name: yourName,
      Address: address,
      Email: email,
      PhoneNumber: phone,
      PickupDateTime: toIST(pickupDate),
      DeliveryDateTime: toIST(deliveryDate),
    };
    await submitSchedulePickupForm(newEntry);
  }

  async function submitSchedulePickupForm(row) {
    try {
      const sheet = await createDocumentObjectByStoreLocation(store);
      await sheet.addRow(row);
      setIsRequested(false);
      showToast('success', 'Success', 'Your pickup has been scheduled.');
      closeModal();
    } catch (e) {
      setIsRequested(false);
      showToast('error', 'Error', 'Oops! Something went wrong.');
    }
  }

  async function createDocumentObjectByStoreLocation(location) {
      try {
          if (location === 'Velachery') {
              await doc.useServiceAccountAuth({
                  client_email: CLIENT_EMAIL,
                  private_key: PRIVATE_KEY.replace(/\\n/g, '\n')
                  });
                  // loads document properties and worksheets
                  await doc.loadInfo();
                  const sheet = doc.sheetsById[SCHEDULE_PICKUP_SHEET_ID];
                  return sheet;
              } else if (location === 'Anna Nagar') {
                  await adoc.useServiceAccountAuth({
                      client_email: A_CLIENT_EMAIL,
                      private_key: A_PRIVATE_KEY.replace(/\\n/g, '\n')
                  });
                  await adoc.loadInfo();
                  const sheet = adoc.sheetsById[A_SCHEDULE_PICKUP_SHEET_ID];
                  return sheet;
              } else {
                  throw new Error("Invalid store location");
              }
      } catch (error) {
          throw new Error("Error creating document object: " + error.message);
      }
  }

  const toIST = (date) => {
    if (!date) return null;
    const utcOffset = 5.5 * 60; // IST is UTC+5:30
    return new Date(date.getTime() + utcOffset * 60 * 1000);
  };

  // Check if today is after 7 PM
  const isAfter7PM = now.getHours() >= 19;
  // If it's after 7 PM today, set minDate to tomorrow
  const pickupMinDate = isAfter7PM ? new Date(now.setDate(now.getDate() + 1)) : now;
  // Ensure the time of `minDate` is always 00:00 (midnight) so that the date is correctly compared
  pickupMinDate.setHours(0, 0, 0, 0);

  // Delivery date must be at least 2 days after pickupMinDate
  const deliveryMinDate = new Date(pickupMinDate);
  deliveryMinDate.setDate(deliveryMinDate.getDate() + 2);
  deliveryMinDate.setHours(0, 0, 0, 0);

  const getTimeBoundsForDate = (selectedDate) => {
    const minTime = new Date(selectedDate);
    const maxTime = new Date(selectedDate);

    minTime.setHours(10, 0, 0, 0); // 10:00 AM
    maxTime.setHours(19, 0, 0, 0); // 7:00 PM

    return { minTime, maxTime };
  };

  const clampTime = (date) => {
    const clamped = new Date(date);
    const hour = clamped.getHours();

    if (hour < 10) clamped.setHours(10, 0, 0, 0);
    else if (hour >= 19) clamped.setHours(18, 59, 59, 999);

    return clamped;
  };

  const { minTime: pickupMinTime, maxTime: pickupMaxTime } = getTimeBoundsForDate(pickupDate || pickupMinDate);
  const { minTime: deliveryMinTime, maxTime: deliveryMaxTime } = getTimeBoundsForDate(deliveryDate || deliveryMinDate);

  return (
    <div className={classes.modal_container}>
      <Toast ref={toast} position="top-right" />
      <Button label={props.text} onClick={openModal} />
      <Dialog header="🚲 Schedule Pickup" visible={modalIsOpen} onHide={closeModal} modal className="p-fluid">
        <form className={classes.modal_form} onSubmit={handleSubmit} id="schedulePickupForm">
          {formFields.map((field, i) => (
            <div className={classes.form_input} key={i}>
              <label>{field}</label>
              {field === 'Email' ? (
                <InputText
                  type="email"
                  id={field}
                  value={email}
                  onChange={(e) => {getMethod(e, field); handleTouch(field);}}
                  required
                />
              ) : field === 'Phone Number' ? (
                <InputText
                  type="number"
                  id={field}
                  value={phone}
                  onChange={(e) => {getMethod(e, field); handleTouch(field);}}
                  required
                />
              ) : (
                <InputText
                  type="text"
                  id={field}
                  value={field === 'Name' ? yourName : address}
                  onChange={(e) => {getMethod(e, field); handleTouch(field);}}
                  required
                />
              )}
              {touchedFields[field] && formErrors[field.toLowerCase()] && (
                <small className={classes.p_error}>{formErrors[field.toLowerCase()]}</small>
              )}
            </div>
          ))}

          <div className={classes.form_input}>
            <label htmlFor="store">Preferred Store</label>
              <Dropdown
                id="store"
                value={store}
                onChange={(e) => {
                  setStore(e.value);
                  handleTouch('store');
                }}
                options={stores}
                placeholder="Select a store"
                required
                // className="p-dropdown-sm"
              />
              {touchedFields['store'] && formErrors['store'] && (
                <small className={classes.p_error}>{formErrors['store']}</small>
              )}
          </div>

          <div className={classes.date_time_picker_container}>
            <div className={classes.date_time_picker}>
              <label>Pickup Date Time</label>
              <Calendar
                value={pickupDate}
                onChange={(e) => {
                  // const clamped = clampTimeToRange(e.value);
                  // setPickupDate(e.value);
                  const clamped = clampTime(e.value);
                  setPickupDate(clamped);
                  validateDeliveryDateTime(e.value);
                  handleTouch('pickupDate')
                }}
                showTime
                hourFormat="12"
                showIcon
                minDate={pickupMinDate}
                minTime={pickupMinTime}
                maxTime={pickupMaxTime}
                dateFormat="dd/mm/yy"
                required
              />
             {touchedFields['pickupDate'] && formErrors.pickupDate && (
                <small className={classes.p_error}>{formErrors.pickupDate}</small>
              )}
            </div>

            <div className={classes.date_time_picker}>
              <label>Delivery Date Time</label>
              <Calendar
                value={deliveryDate}
                onChange={(e) => {
                  // const clamped = clampTimeToRange(e.value);
                  const clamped = clampTime(e.value);
                  setDeliveryDate(clamped);
                  // setDeliveryDate(e.value); 
                  handleTouch('deliveryDate')
                }}
                showTime
                hourFormat="12"
                showIcon
                minDate={deliveryMinDate}
                minTime={deliveryMinTime}
                maxTime={deliveryMaxTime}
                dateFormat="dd/mm/yy"
                required
              />
            </div>
          </div>

          <div className={classes.form_button}>
            <Button 
            label={
              isRequested ? 'Scheduling...' : 'Schedule Pickup' 
            } 
            type="submit" 
            disabled={!isFormValid || isRequested} />
          </div>
        </form>
      </Dialog>
    </div>
  );
};

export default SchedulePickupPage;
